import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { get } from 'lodash';
import * as sitesActions from '../actions/sites';
import * as groupsActions from '../actions/groups';
import { hasActiveFeatureFlag } from '../helpers/fetureFlagsHelper';
import { FF_USER_GROUPS } from '../components/FeatureFlagWrapper';
import UsersTable from '../components/UsersTable';
import routes from '../routes';
import * as authService from '../services/auth';
import { Roles } from '../models/roles';
import * as usersActions from '../actions/users';
import * as sitesApi from '../api/sites';
import * as usersApi from '../api/users';
import { getUserFullName } from '../helpers/user';
import * as authActions from '../actions/auth';
import snackbarHelper from '../helpers/snackbarHelper';
import FABContext from '../context/FABContext/FABContext';
import { setFABActions } from '../helpers/fab';
import { transformRoleToApi } from '../models/users';
import * as tableActions from '../actions/tableOptions';

const TABLE_ID = '#users/users';
export const RECEIVER_TYPE = 'Receiving Agency';
export const FOOD_DONOR_TYPE = 'Food Donor';

class UsersListView extends Component {
  constructor(props) {
    super(props);

    const { activeUser } = props;

    this.state = {
      isAllowedToCreateUser: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
      ]),
      isAllowedToEditUser: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
      ]),
      isAllowedToDeleteUser: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [Roles.Admin]),
      isAllowedToReinstateUser: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [Roles.Admin]),
      isAllowedToDeleteSiteCoordinator: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [Roles.SiteDirector]),
      isAllowedToSentInvitations: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
      ]),
      isAllowedToSendEmail: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
      ]),
      isAllowedToSendSms: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [
        Roles.Admin,
        Roles.NationalSiteDirector,
        Roles.SiteDirector,
        Roles.SiteCoordinator,
      ]),
      isAllowedToImpersonateUser: authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [Roles.Admin]),
      isAllowedToSeeDifferentSiteAlert:
        authService.hasAnyRoleInCurrentlySelectedSite(activeUser, [Roles.Admin, Roles.NationalSiteDirector]) ||
        authService.getUserSitesByRoles(activeUser.role_assignments, Roles.SiteDirector).length > 1,
      usersWhoLeft: [],
    };
  }

  componentDidMount() {
    const { fetchSiteUsers, fetchSiteReceivers, fetchSiteDonorsLocations, fetchGroups, site, hasUserGroupsFF } =
      this.props;

    fetchSiteUsers(site.id);
    fetchSiteReceivers(site.id);
    fetchSiteDonorsLocations(site.id);

    if (hasUserGroupsFF) {
      fetchGroups(site.id);
    }

    this.fetchUsersWhoLeft();
  }

  // users who clicked "Leave Food Rescue US" while being a rescuer at the site, listed (deactivated) for admins only,
  // so they can be reinstated
  fetchUsersWhoLeft = async () => {
    const { site } = this.props;
    const { isAllowedToReinstateUser } = this.state;

    if (!isAllowedToReinstateUser) {
      return;
    }

    let json;

    try {
      const res = await sitesApi.getSiteUsersWhoLeft(site.id);

      json = await res.json();
    } catch (error) {
      snackbarHelper.error(error.message || 'Unable to load the users who left Food Rescue US.');

      return;
    }

    this.setState({
      usersWhoLeft: json.data.map(user => ({
        ...user,
        active: 0,
        role_assignments: [],
        left_at: user.deleted_at,
      })),
    });
  };

  handleReinstateUser = async user => {
    const { site, fetchSiteUsers } = this.props;

    try {
      await usersApi.reinstateUser(user.id);
    } catch (error) {
      snackbarHelper.error(error.message || 'Unable to reinstate the user.');

      return;
    }

    snackbarHelper.success(`${getUserFullName(user)} has been reinstated`);

    await Promise.all([fetchSiteUsers(site.id), this.fetchUsersWhoLeft()]);
  };

  sendEmailInvitation = user => {
    const { site, sendInvitationEmail } = this.props;

    sendInvitationEmail(user, site.id);

    snackbarHelper.success(`Invitation has been ${user.invitation_sent ? 'resent' : 'sent'}`);
  };

  handleSendEmailButtonClick = (users, selectedGroup) => {
    const { history } = this.props;

    if (users.every(r => !r.email_verified)) {
      snackbarHelper.info(
        `You don't have any recipients with verified e-mail address selected` +
          ` therefore you will not be able to send the e-mail.`
      );
    }

    return history.push(routes.sendEmail, {
      usersIds: users.map(r => r.id),
      ...(selectedGroup ? { groupId: selectedGroup.id, groupName: selectedGroup.name } : {}),
    });
  };

  handleSendSMSButtonClick = (users, selectedGroup) => {
    const { history } = this.props;

    if (users.every(r => !r.phone_verified)) {
      snackbarHelper.info(
        `You don't have any recipients with verified phone# selected` +
          ` therefore you will not be able to send the SMS.`
      );
    }

    return history.push(routes.sendSms, {
      usersIds: users.map(r => r.id),
      ...(selectedGroup ? { groupId: selectedGroup.id, groupName: selectedGroup.name } : {}),
    });
  };

  handleImpersonateUserButtonClick = userId => {
    const { users, impersonateUser, history } = this.props;

    impersonateUser(userId);

    setFABActions(users.byId[userId], history, this.context);
  };

  handleDeleteSiteCoordinator = async (user) => {
    const { site, updateUser, fetchSiteUsers } = this.props;

    await updateUser(
      user.id,
      {
        roles_to_detach: [
          { role_name: transformRoleToApi(Roles.SiteCoordinator), site_id: site.id },
        ],
      }
    );

    return fetchSiteUsers(site.id);
  };

  renderCustomTableToolbar = () => {
    const { history } = this.props;
    const { isAllowedToCreateUser } = this.state;

    if (!isAllowedToCreateUser) {
      return null;
    }

    return (
      <Button
        startIcon={<Add />}
        type="button"
        variant="contained"
        color="primary"
        size="small"
        onClick={() => history.push(routes.usersAdd)}
        data-testid="add-new-user-button"
      >
        Add New Coordinator
      </Button>
    );
  };

  handleShowUsersWithAllSelectedRoles = (value) => {
    const { previousTableState, setUserTableOptions, activeUser } = this.props;

    setUserTableOptions(activeUser.id, TABLE_ID, { ...previousTableState, showUsersWithAllSelectedRoles: !!value });
  };

  render() {
    const {
      usersList,
      deleteUser,
      isFetchingUsers,
      site,
      previousTableState,
      groupsList,
      receiversList = [],
      foodDonorList = [],
      affiliationLoading,
      locationState,
    } = this.props;
    const {
      isAllowedToEditUser,
      isAllowedToDeleteUser,
      isAllowedToReinstateUser,
      isAllowedToDeleteSiteCoordinator,
      isAllowedToSentInvitations,
      isAllowedToSendEmail,
      isAllowedToSendSms,
      isAllowedToImpersonateUser,
      isAllowedToSeeDifferentSiteAlert,
      usersWhoLeft,
    } = this.state;

    const affiliationsList = [
      ...foodDonorList.map((fd) => ({ ...fd, type: FOOD_DONOR_TYPE })),
      ...receiversList.map((ra) => ({ ...ra, type: RECEIVER_TYPE })),
    ];

    if (isFetchingUsers) {
      return <div>Loading users</div>;
    }

    return (
      <Grid container>
        <Grid item xs={12}>
          <UsersTable
            tableId={TABLE_ID}
            title="People"
            customToolbar={this.renderCustomTableToolbar}
            sendInvitation={this.sendEmailInvitation}
            onUserDelete={deleteUser}
            onSiteCoordinatorDelete={this.handleDeleteSiteCoordinator}
            onUserReinstate={this.handleReinstateUser}
            onSendEmailButtonClick={this.handleSendEmailButtonClick}
            onSendSMSButtonClick={this.handleSendSMSButtonClick}
            onImpersonateUserButtonClick={this.handleImpersonateUserButtonClick}
            users={usersWhoLeft.length ? [...(usersList || []), ...usersWhoLeft] : usersList}
            site={site}
            groupsList={groupsList}
            affiliationsList={affiliationsList}
            affiliationLoading={affiliationLoading}
            showRolesColumn
            showVerifiedColumn
            showEditUserButton={isAllowedToEditUser}
            showDeleteUserButton={isAllowedToDeleteUser}
            showDeleteSiteCoordinatorButton={isAllowedToDeleteSiteCoordinator}
            showReinstateUserButton={isAllowedToReinstateUser}
            showSentInvitationUserButton={isAllowedToSentInvitations}
            showSelectableRows={isAllowedToSendEmail || isAllowedToSendSms}
            showSendEmailButton={isAllowedToSendEmail}
            showSendSmsButton={isAllowedToSendSms}
            showImpersonateUserButton={isAllowedToImpersonateUser}
            showDifferentSiteAlert={isAllowedToSeeDifferentSiteAlert}
            previousTableState={previousTableState}
            handleShowUsersWithAllSelectedRoles={this.handleShowUsersWithAllSelectedRoles}
            locationState={locationState}
          />
        </Grid>
      </Grid>
    );
  }
}

const mapStateToProps = ({
  app: { site, loggedInUser, impersonating },
  entities: {
    sites: { users, donorsLocations, receivers },
    groups,
    featureFlags,
  },
  tableOptions,
}) => {
  const activeUser = impersonating || loggedInUser;
  const featureFlagsList = Object.values(featureFlags.byId);
  const hasUserGroupsFF = hasActiveFeatureFlag(FF_USER_GROUPS, site, featureFlagsList);
  const siteGroupIds = (groups && groups.bySiteId && groups.bySiteId[site.id]) || [];
  const groupsList = hasUserGroupsFF ? siteGroupIds.map(id => groups.byId[id]).filter(Boolean) : [];

  return {
    site,
    activeUser: activeUser,
    isFetchingUsers: users.inflight,
    users: users,
    usersList: users.bySiteId[site.id],
    affiliationLoading: receivers.inflight || donorsLocations.inflight,
    foodDonorList: donorsLocations.bySiteId[site.id],
    receiversList: receivers.bySiteId[site.id],
    groupsList,
    hasUserGroupsFF,
    previousTableState: get(tableOptions, [activeUser.id, TABLE_ID], {}),
  };
};
const mapDispatchToProps = dispatch => ({
  fetchSiteUsers: siteId => dispatch(sitesActions.fetchSiteUsersIfNeeded(siteId)),
  fetchSiteReceivers: siteId => dispatch(sitesActions.fetchSiteReceiversIfNeeded(siteId)),
  fetchSiteDonorsLocations: siteId => dispatch(sitesActions.fetchSiteDonorsLocationsIfNeeded(siteId)),
  fetchGroups: siteId => dispatch(groupsActions.fetchSiteGroups(siteId)),
  deleteUser: user => dispatch(usersActions.deleteUser(user)),
  updateUser: (userId, data, siteId = null) => dispatch(usersActions.updateUser(userId, data, siteId)),
  sendInvitationEmail: (user, siteId) => dispatch(usersActions.resendUserInvitationEmail(user.id, siteId)),
  impersonateUser: userId => dispatch(authActions.changeUser(userId)),
  setUserTableOptions: (userId, tableId, data) => dispatch(tableActions.setUserTableOptions(userId, tableId, data)),
});

UsersListView.contextType = FABContext;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withRouter(UsersListView)
);
